import { useForm, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  SidebarWrapperFooter,
  SidebarWrapperForm,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { useDeskId } from '@/hooks/useDeskId';
import { RULE_FORM_ID } from '@/modules/rules/constants';
import { useRuleFormGuards } from '@/modules/rules/hooks/useRuleFormGuards';
import { useRuleMutations } from '@/modules/rules/hooks/useRules';
import { removeFormState } from '@/redux/forms/actions';
import { Rule } from '@/shared/types/rules';
import { createYupResolver } from '@/util/forms/createYupResolver';

import { createRenameRuleSchema, RuleNameValues } from './ruleFormSchema';
import { RuleNameFields } from './RuleNameFields';

type RenameRuleFormProps = { rule: Rule; onClose: () => void; onOpenEdit: () => void };

export const RenameRuleForm = ({ rule, onClose, onOpenEdit }: RenameRuleFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const deskId = useDeskId();
  const { saveRule, saveStatus } = useRuleMutations(deskId);

  const form = useForm<RuleNameValues>({
    resolver: createYupResolver<RuleNameValues>(createRenameRuleSchema(deskId, rule.name)),
    defaultValues: { name: rule.name, description: rule.description ?? '' },
    mode: 'onChange',
  });
  const { isDirty } = useFormState({ control: form.control });

  useRuleFormGuards({ isDirty, onClose });

  const onSubmit = ({ name, description }: RuleNameValues) => {
    saveRule(
      { ...rule, name, description },
      {
        onSuccess: () => {
          dispatch(removeFormState({ formId: RULE_FORM_ID }));
          onOpenEdit();
        },
      }
    );
  };

  return (
    <Form {...form}>
      <SidebarWrapperForm onSubmit={form.handleSubmit(onSubmit)} noValidate className="pr-4">
        <RuleNameFields />
        <SidebarWrapperFooter>
          <Button type="submit" disabled={!isDirty} isLoading={saveStatus === 'pending'}>
            {t('common.save')}
          </Button>
          <Button type="button" variant="ghost-neutral" onClick={onOpenEdit}>
            {t('common.cancel')}
          </Button>
        </SidebarWrapperFooter>
      </SidebarWrapperForm>
    </Form>
  );
};
